import { SkyDialog, SkyDropdown, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import type { RetryableConnection, RunDetail } from "@shared/types/runs";
import { useConnectors } from "@/hooks/use-connectors";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useDataflowEntities } from "@/hooks/use-dataflows";
import { useRetryTables } from "@/hooks/use-runs";
import { useInstanceConnections } from "@/hooks/use-schedules";
import { newIdempotencyKey } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { absolutePath } from "@/lib/routes";
import { searchPending, useServerTableSearch } from "./serverTableSearch";
import { TablePicker, type TablePickerRow } from "./TablePicker";
import { SKEW_SENTENCE, SKEW_TITLE, howReadAgain, tablesWord, whatHappened } from "./tableRuns";

export { SKEW_SENTENCE };

/**
 * "Retry N tables" (product gaps row 2, design section 4.5): a NEW run of one connection that
 * reads only the tables still owed from this run, each from its own watermark, up to now. A
 * table whose LOAD failed is loaded again from the files this run staged, with no new extract
 * (owner answer of 2026-09-28), while no later import has landed it; the grid says which, per
 * table, before anything is pressed.
 *
 * The tables that did not land start ticked; the connection's other selected tables are listed
 * unticked, so the reader can widen the retry. With the set as offered the request names only
 * the connection, and the server picks what did not land, so a table that landed since the page
 * loaded is never read twice. Mechanics as `RunNowDialog`: one Idempotency-Key per open, one POST
 * per click, a refusal stays inside the dialog, and success goes to the new run's page.
 */

/** The most tables the picker lists from the connection's selection; a search narrows the rest. */
const PICKER_PAGE = 200;

/** The missed tables come with the run, so they are listed even when the rest cannot be read. */
const OTHER_TABLES_ERROR =
  "The other selected tables could not be loaded. The tables this run missed are listed.";

/**
 * The most missed names asked about in one request (`names=` on the entities route): one page,
 * and an encoded query of at most 8 KB, so the address and the bearer header stay under Node's
 * 16 KB header limit.
 */
const MISSED_QUERY_NAMES = 200;
const MISSED_QUERY_BYTES = 8_000;

/** What the `names=` part of the query adds, encoded exactly as `entitiesPath` sends it. */
function namesQueryLength(names: readonly string[]): number {
  return new URLSearchParams(names.map((name) => ["names", name])).toString().length;
}

/** What the page did not hold, and what of that is known to be other tables. */
interface HiddenRows {
  /** The server counted the list as longer than the page it sent. */
  cut: boolean;
  /**
   * Hidden rows that cannot all be missed tables: the rows the server counted but did not send,
   * less every missed table not on the page (the most of them that could be among those rows).
   */
  othersAtLeast: number;
}

/**
 * What the grid lists when the other tables are cut at one page: every missed table, and a page
 * of the rest. `othersTotal` is the server's total less the missed tables it still counts as
 * selected (exact, from the `names=` request). Without it (too many missed to ask, or that
 * request not answered) the note gives no total: it says how many are shown when other tables
 * are proved hidden, says only that they may not all be shown when the page is cut and holds
 * other tables, and says nothing when every hidden row may be a missed table already listed.
 */
function retryListNote(
  othersShown: number,
  othersTotal: number | null,
  hidden: HiddenRows,
  searching: boolean,
): string | null {
  const listed = searching
    ? "Every table this run missed that matches is listed."
    : "Every table this run missed is listed.";
  const next = searching ? "Type more to narrow the list." : "Search to find a table.";
  const which = searching ? "matching" : "selected";
  if (othersTotal === null && hidden.othersAtLeast === 0) {
    // A cut page of missed tables only, with no other table provably hidden, is the usual run in
    // which every table failed (300 missed of 300): there the hedged sentence would be noise. The
    // cost is silence in the rare run with more than 200 missed, some unselected since.
    return hidden.cut && othersShown > 0
      ? `${listed} Other ${which} tables may not all be shown. ${next}`
      : null;
  }
  if (othersTotal !== null && othersTotal <= othersShown) return null;
  if (othersShown === 0) return `${listed} Other ${which} tables are not shown. ${next}`;
  const shown =
    othersTotal === null
      ? othersShown.toLocaleString("en-US")
      : `${othersShown.toLocaleString("en-US")} of ${othersTotal.toLocaleString("en-US")}`;
  const verb = othersShown === 1 ? "is" : "are";
  return `${listed} Of the other ${which} tables, ${shown} ${verb} shown. ${next}`;
}

export interface RetryTablesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  run: Pick<RunDetail, "runId" | "retryable">;
  connectionNames: Map<string, string>;
}

export function RetryTablesDialog({
  open,
  onOpenChange,
  run,
  connectionNames,
}: RetryTablesDialogProps) {
  const [, navigate] = useLocation();
  const notice = useNotice();
  const inline = useInlineError();
  const retryTables = useRetryTables(run.runId);
  const zone = useDisplayTz();
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);
  const inFlight = useRef(false);
  const connections = useMemo((): RetryableConnection[] => run.retryable ?? [], [run.retryable]);
  const [dataflowId, setDataflowId] = useState(connections[0]?.dataflowId ?? "");
  const missed = useMemo(
    () => connections.find((row) => row.dataflowId === dataflowId)?.tables ?? [],
    [connections, dataflowId],
  );
  const missedNames = useMemo(() => missed.map((table) => table.entityName), [missed]);
  const [chosen, setChosen] = useState<string[]>(missedNames);

  const tableSearch = useServerTableSearch();
  const entities = useDataflowEntities(dataflowId || null, {
    search: tableSearch.settled,
    selected: "selected",
    entityType: "all",
    page: 1,
    pageSize: PICKER_PAGE,
  });
  // Which missed tables are still selected and match the search, wherever they sort: the
  // server's total counts them, and its match also reads display names, which the run does not
  // carry. Asked only when the names fit one request.
  const missedAskable =
    missedNames.length > 0 &&
    missedNames.length <= MISSED_QUERY_NAMES &&
    namesQueryLength(missedNames) <= MISSED_QUERY_BYTES;
  const missedSelected = useDataflowEntities(missedAskable && dataflowId ? dataflowId : null, {
    search: tableSearch.settled,
    selected: "selected",
    entityType: "all",
    page: 1,
    pageSize: MISSED_QUERY_NAMES,
    names: missedNames,
  });
  // #1077: a connection of a connector that cannot import incrementally reads every row again.
  // Its connector says so; one the catalog does not list (deactivated) is read by the summary's
  // refresh type, which the server reports as `full` for such a connector.
  const connectionList = useInstanceConnections(open);
  const connectorList = useConnectors(open);
  const readsEveryRow = useMemo((): boolean => {
    const connection = connectionList.data?.find((row) => row.id === dataflowId);
    if (!connection) return false;
    const connector = connectorList.data?.find((row) => row.name === connection.connectorName);
    return connector ? !connector.isIncrementalSupported : connection.refreshType === "full";
  }, [connectionList.data, connectorList.data, dataflowId]);
  // The `names=` answer is used only once it is in and did not fail; until then, or when it cannot
  // be asked, the page stands in, as it did before that request existed.
  const missedKnown = missedAskable && !missedSelected.isError && missedSelected.data !== undefined;
  const pending =
    searchPending(tableSearch, entities) ||
    (missedKnown && searchPending(tableSearch, missedSelected));
  // The missed tables the server matched for this search (none while it is being answered).
  const served = useMemo(() => {
    if (pending) return new Set<string>();
    const items = missedKnown ? missedSelected.data?.items : entities.data?.items;
    return new Set((items ?? []).map((entity) => entity.name));
  }, [pending, missedKnown, missedSelected.data, entities.data]);
  // The tables this run missed are listed in full, searched like the server's page: the run
  // records a missed table by name only, so a name match here, or the server's own match (which
  // also reads the display name) when its answer names the table.
  const missedShown = useMemo(() => {
    const needle = tableSearch.settled.toLowerCase();
    return needle
      ? missed.filter(
          (table) =>
            table.entityName.toLowerCase().includes(needle) || served.has(table.entityName),
        )
      : missed;
  }, [missed, tableSearch.settled, served]);
  const landed = useMemo((): string[] => {
    const byName = new Set(missed.map((table) => table.entityName));
    return (entities.data?.items ?? [])
      .map((entity) => entity.name)
      .filter((name) => !byName.has(name));
  }, [missed, entities.data]);
  const rows = useMemo((): TablePickerRow[] => {
    // While a typed search is answered, the missed tables stay and the rest waits for it.
    const others = pending ? [] : landed;
    return [
      ...missedShown.map((table) => ({
        name: table.entityName,
        happened: whatHappened(table),
        how: howReadAgain(table, readsEveryRow, zone),
      })),
      ...others.map((name) => ({
        name,
        happened: whatHappened(null),
        how: howReadAgain(null, readsEveryRow, zone),
      })),
    ];
  }, [missedShown, landed, pending, readsEveryRow, zone]);
  // The server's total less the missed tables it counts (still selected and matching), exactly.
  const othersTotal =
    missedKnown && missedSelected.data
      ? Math.max(0, (entities.data?.total ?? 0) - missedSelected.data.total)
      : null;
  const hidden = useMemo((): HiddenRows => {
    const items = entities.data?.items ?? [];
    const total = entities.data?.total ?? 0;
    const onPage = new Set(items.map((entity) => entity.name));
    const missedOffPage = missedNames.filter((name) => !onPage.has(name)).length;
    return {
      cut: total > items.length,
      othersAtLeast: Math.max(0, total - items.length - missedOffPage),
    };
  }, [entities.data, missedNames]);
  const connectionName = connectionNames.get(dataflowId) ?? "this connection";
  const unchanged =
    chosen.length === missedNames.length && missedNames.every((name) => chosen.includes(name));

  const submit = async (): Promise<void> => {
    if (inFlight.current || chosen.length === 0 || !dataflowId) return;
    inFlight.current = true;
    inline.clear();
    try {
      const names = [...chosen].sort((a, b) => a.localeCompare(b));
      const result = await retryTables.mutateAsync({
        body: unchanged ? { dataflowId } : { dataflowId, entityNames: names },
        idempotencyKey: `${idempotencyKey}:${dataflowId}`,
      });
      const runPage = `/runs/${result.runId}`;
      onOpenChange(false);
      notice.success(
        `A new import of ${tablesWord(result.tableCount)} of ${connectionName} is queued.`,
        { forPath: runPage, title: "Tables retried" },
      );
      navigate(absolutePath(runPage));
    } catch (error) {
      inline.show(error, "The tables could not be retried.");
    } finally {
      inFlight.current = false;
    }
  };

  const label = `Retry ${tablesWord(chosen.length)}`;
  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Retry tables of ${connectionName}?`}
      description="A new import reads only the chosen tables. This run keeps its outcome."
      size="large"
      testId="retry-tables-dialog"
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: retryTables.isPending ? "Retrying…" : label,
        onClick: submit,
        disabled: retryTables.isPending || chosen.length === 0,
        loading: retryTables.isPending,
      }}
      secondaryAction={{
        label: "Cancel",
        onClick: () => onOpenChange(false),
        disabled: retryTables.isPending,
      }}
    >
      <div className="flex flex-col gap-4">
        {connections.length > 1 ? (
          <SkyDropdown
            label="Connection"
            options={connections.map((row) => ({
              value: row.dataflowId,
              text: `${connectionNames.get(row.dataflowId) ?? row.dataflowId} (${tablesWord(row.tables.length)} did not land)`,
            }))}
            selectedOptions={dataflowId ? [dataflowId] : []}
            onOptionSelect={(values) => {
              const next = values[0] ?? "";
              setDataflowId(next);
              setChosen(
                connections
                  .find((row) => row.dataflowId === next)
                  ?.tables.map((table) => table.entityName) ?? [],
              );
            }}
            testId="retry-tables-connection"
          />
        ) : null}

        <SkyMessageBar
          intent="info"
          layout="multiline"
          title={SKEW_TITLE}
          message={SKEW_SENTENCE}
          testId="retry-tables-skew"
        />

        <TablePicker
          rows={rows}
          selected={chosen}
          onChange={setChosen}
          extraColumns={[
            { key: "happened", header: "What happened" },
            { key: "how", header: "How it is read again" },
          ]}
          ariaLabel={`Tables of ${connectionName} to retry`}
          loading={entities.isLoading}
          searchPending={pending}
          note={
            pending
              ? null
              : retryListNote(landed.length, othersTotal, hidden, Boolean(tableSearch.settled))
          }
          error={entities.isError ? OTHER_TABLES_ERROR : null}
          onSearchChange={tableSearch.setSearch}
          testId="retry-tables-picker"
        />

        <SkyText size={200} color="muted" block>
          The retry is recorded in the audit trail against your account.
        </SkyText>

        <InlineErrorBar
          error={inline.error}
          title={inline.error?.retryable ? undefined : "The tables were not retried"}
          testId="retry-tables-error"
        />
      </div>
    </SkyDialog>
  );
}
